import { useMemo } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { AgreementProductReadModel } from '@client/api';
import {
  isDefinedNumber,
  isDefinedString,
  TableCell,
  TableRow,
  Text,
  toIntervalString,
  toMoneyStringOrUndefined,
  toNumberString,
  useTotalAmountString,
} from '@client/toolkit';

type AgreementProductItemProps = {
  agreementProduct?: AgreementProductReadModel;
  isFetching?: boolean;
};

export function AgreementProductItem({ agreementProduct, isFetching }: AgreementProductItemProps) {
  const { t: tGlobal } = useTranslation('global');
  const totalAmount = useTotalAmountString(agreementProduct?.yearlyTotalAmount, agreementProduct?.oneTimeTotalAmount);

  // Check if product is expired (stop date is in the past)
  const isExpired = useMemo(() => {
    const endDate = agreementProduct?.interval?.end;
    if (!endDate) return false;
    return dayjs(endDate).isBefore(dayjs(), 'day');
  }, [agreementProduct?.interval?.end]);

  const effectiveUnitAmount = useMemo(() => {
    return agreementProduct?.overwrittenUnitAmount ?? agreementProduct?.price?.unitAmount;
  }, [agreementProduct?.overwrittenUnitAmount, agreementProduct?.price?.unitAmount]);

  // Calculate number of months between start and end dates (inclusive - "til og med")
  const monthsInPeriod = useMemo(() => {
    const intervalStart = agreementProduct?.interval?.start;
    const intervalEnd = agreementProduct?.interval?.end;
    if (!intervalStart || !intervalEnd) return null;
    const start = dayjs(intervalStart);
    const end = dayjs(intervalEnd).add(1, 'day'); // Add 1 day for inclusive end date
    if (!start.isValid() || !end.isValid()) return null;
    return Math.max(0, end.diff(start, 'month', true));
  }, [agreementProduct?.interval?.start, agreementProduct?.interval?.end]);

  const totalAmountString = useMemo(() => {
    if (!effectiveUnitAmount || !agreementProduct?.quantity) return totalAmount;

    const monthlyTotal = effectiveUnitAmount.amount * agreementProduct.quantity;
    let total = monthlyTotal;
    let suffix = '/år';

    // For recurring products
    if (agreementProduct?.price?.recurring) {
      // If we have a custom period, use that
      if (monthsInPeriod != null && monthsInPeriod < 12) {
        total = monthlyTotal * monthsInPeriod;
        suffix = ` (${Math.round(monthsInPeriod)} mnd)`;
      } else {
        // Otherwise show yearly amount
        total = monthlyTotal * 12;
      }
    } else {
      // For one-time products, show as is without suffix
      suffix = '';
    }

    const moneyStr = toMoneyStringOrUndefined({ amount: total, currency: effectiveUnitAmount.currency });
    return moneyStr ? `${moneyStr}${suffix}` : totalAmount;
  }, [
    effectiveUnitAmount,
    agreementProduct?.quantity,
    agreementProduct?.price?.recurring,
    monthsInPeriod,
    totalAmount,
  ]);

  return (
    <TableRow verticalAlign="middle" className={isExpired ? 'opacity-50' : ''}>
      {/* Produkt */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(agreementProduct?.price?.product?.name)}>
          <strong>{agreementProduct?.price?.product?.name}</strong>
          {isExpired && <span className="ml-2 text-xs text-text-secondary">(Utløpt)</span>}
        </Text>
      </TableCell>

      {/* Antall */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedNumber(agreementProduct?.quantity)}>
          {toNumberString(agreementProduct?.quantity)}
        </Text>
      </TableCell>

      {/* Start */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(agreementProduct?.interval?.start)}>
          {agreementProduct?.interval?.start ? dayjs(agreementProduct.interval.start).format('DD.MM.YYYY') : '-'}
        </Text>
      </TableCell>

      {/* Stopp */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(agreementProduct?.interval?.end)}>
          {agreementProduct?.interval?.end ? dayjs(agreementProduct.interval.end).format('DD.MM.YYYY') : '-'}
        </Text>
      </TableCell>

      {/* Produktpris */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedNumber(agreementProduct?.price?.unitAmount?.amount)}>
          {toMoneyStringOrUndefined(agreementProduct?.price?.unitAmount)}
        </Text>
      </TableCell>

      {/* Overstyrt produktpris */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedNumber(agreementProduct?.overwrittenUnitAmount?.amount)}>
          {toMoneyStringOrUndefined(agreementProduct?.overwrittenUnitAmount)}
        </Text>
      </TableCell>

      {/* Periode */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!agreementProduct?.price}>
          {agreementProduct?.price?.recurring === false
            ? 'Engangs'
            : toIntervalString(agreementProduct?.price?.interval)}
        </Text>
      </TableCell>

      {/* Fakturert */}
      <TableCell loading={isFetching}>
        {agreementProduct?.oneTimeTotalAmount != null ? (
          <Text as="div">{agreementProduct.isInvoiced ? tGlobal('yes') : tGlobal('no')}</Text>
        ) : (
          <Text as="div">-</Text>
        )}
      </TableCell>

      {/* Totalpris */}
      <TableCell align="right" loading={isFetching} className="whitespace-nowrap">
        <Text as="div" isEmpty={!isDefinedString(totalAmountString)}>
          <strong>{totalAmountString}</strong>
        </Text>
      </TableCell>
    </TableRow>
  );
}
